import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined';
import FullscreenExitOutlinedIcon from '@mui/icons-material/FullscreenExitOutlined';
import useDraggablePanel from '../../../hooks/useDraggablePanel';
import { panelSx, titlebarSx } from './noteOptionsSx';
import NoteOptionsPanelContent from './NoteOptionsPanelContent';
import noteOptionsPanelEngine from './Core/NoteOptionsPanelEngine';
import { useNoteOptionsPanel } from './Core/useNoteOptionsPanel';
import { tooltipSxElevated } from '../../../components/shared/jernyTooltipStyling';

/**
 * NoteOptionsPanel
 * @param {object} props
 * @param {boolean} props.open
 * @param {object} props.anchorPos - { top, left }
 * @param {function} props.onClose
 * @param {object} props.entry
 * @param {boolean} props.isTemplate
 * @param {boolean} props.isJson
 * @param {string} [props.panelId='default']
 */
export default function NoteOptionsPanel({ open, anchorPos, onClose, entry, isTemplate, isJson, panelId = 'default' }) {
    const { pos, setPos, dragHandleProps } = useDraggablePanel(anchorPos ?? { top: 60, left: 200 });
    const [posReady, setPosReady] = useState(false);
    const wasOpen = useRef(false);
    const { inFocusMode } = useNoteOptionsPanel(panelId);

    useEffect(() => {
        if (open && !wasOpen.current && anchorPos) {
            setPos(anchorPos);
            setPosReady(true);
        }
        if (!open) setPosReady(false);
        wasOpen.current = open;
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open, onClose]);

    if (!open || !posReady) return null;

    const handleFocus = () => {
        noteOptionsPanelEngine.toggleFocus(panelId);
        onClose();
    };

    const panel = (
        <Box sx={{ ...panelSx, top: pos.top, left: pos.left }}>
            <Box sx={titlebarSx} {...dragHandleProps}>
                <DragIndicatorIcon sx={{ fontSize: 14, color: '#a3d1ff', mr: '4px', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#a3d1ff', flex: 1 }}>Note Options</Typography>
                <Tooltip title={inFocusMode ? 'Exit focus mode' : 'Focus mode'} componentsProps={tooltipSxElevated}>
                    <IconButton size="small" onClick={handleFocus} sx={{ color: '#a3d1ff', '&:hover': { color: '#66b2ff' }, p: '2px' }}>
                        {inFocusMode ? (
                            <FullscreenExitOutlinedIcon sx={{ fontSize: 15 }} />
                        ) : (
                            <FullscreenOutlinedIcon sx={{ fontSize: 15 }} />
                        )}
                    </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={onClose} sx={{ color: '#a3d1ff', '&:hover': { color: '#ff6b6b' }, p: '2px' }}>
                    <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
            </Box>

            <NoteOptionsPanelContent entry={entry} isTemplate={isTemplate} isJson={isJson} onClose={onClose} panelId={panelId} />
        </Box>
    );

    return createPortal(panel, document.body);
}
